import { ProductDataType } from "@/api/ProductsApi";
import { ProductRelatedAttributes } from "@/formTypes/ProductFormDataType";
import { buildImportPayload } from "./core/payload";
import { ImportPayloadRow } from "./core/types";
import { ProductResolvedRow } from "./resolveProductRows";

/** Comentario del movimiento de inventario de un producto recien importado. */
export const STOCK_LOG_CREATE_COMMENT = "Importacion de productos";

/** Comentario del movimiento cuando la importacion ajusta las existencias de uno existente. */
export const STOCK_LOG_UPDATE_COMMENT = "Ajuste de inventario por importacion de productos";

/**
 * Bitacoras que la lambda tiene que escribir junto al producto.
 *
 * Se calculan aqui y no en la lambda porque el navegador ya tiene cargado el producto
 * actual para detectar duplicados: evitan un GetItem por fila.
 */
export type ProductImportMeta = {
    stock?: { previous: number; change: number; next: number; comments: string };
    cost?: { cost: number };
};

/**
 * Atributos que no viajan a la lambda: son relaciones que el formulario carga para
 * mostrar, no columnas del modelo.
 */
const RELATION_ATTRIBUTES = new Set<string>(ProductRelatedAttributes as string[]);

/** Convierte las filas resueltas en el payload que espera la lambda de importacion. */
export const buildProductImportPayload = (params: {
    rows: ProductResolvedRow[];
    providedFields: Map<number, Set<string>>;
    existingProducts: ProductDataType[];
}): ImportPayloadRow[] => {
    const { rows, providedFields, existingProducts } = params;

    const byId = new Map<string, ProductDataType>();
    existingProducts.forEach((product) => {
        if (product.id) byId.set(product.id, product);
    });

    const providedOf = (row: ProductResolvedRow): Set<string> =>
        providedFields.get(row.rowIndex) || new Set<string>();

    const payload = buildImportPayload<ProductDataType & object>(rows, RELATION_ATTRIBUTES, (row) =>
        buildMeta(row, byId, providedOf(row))
    );

    // Al actualizar solo se mandan los atributos que venian en el archivo: los defaults
    // (existencias 0, minimo 0, IVA...) son para productos nuevos y no deben pisar lo que
    // el producto ya tenia configurado.
    return payload.map((entry, index) => {
        if (entry.action !== "update") return entry;

        const provided = providedOf(rows[index]);
        const data = Object.fromEntries(
            Object.entries(entry.data).filter(([key]) => provided.has(key))
        );

        return { ...entry, data };
    });
};

const buildMeta = (
    row: ProductResolvedRow,
    byId: Map<string, ProductDataType>,
    provided: Set<string>
): ProductImportMeta | undefined => {
    if (row.action === "create") {
        const stock = Number(row.data.existencias ?? 0);
        return {
            stock: { previous: 0, change: stock, next: stock, comments: STOCK_LOG_CREATE_COMMENT },
            cost: { cost: Number(row.data.costo ?? 0) },
        };
    }

    const existing = row.existingId ? byId.get(row.existingId) : undefined;
    if (!existing) return undefined;

    const meta: ProductImportMeta = {};

    // Solo se registra movimiento si el archivo traia la columna: los defaults no cuentan.
    if (provided.has("existencias")) {
        const previous = Number(existing.existencias ?? 0);
        const next = Number(row.data.existencias);
        if (next !== previous) {
            meta.stock = { previous, change: next - previous, next, comments: STOCK_LOG_UPDATE_COMMENT };
        }
    }

    if (provided.has("costo")) {
        const cost = Number(row.data.costo);
        if (cost !== Number(existing.costo ?? 0)) meta.cost = { cost };
    }

    return meta.stock || meta.cost ? meta : undefined;
};
